import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCodeBranch, faLink, faLinkSlash, faFileExport } from "@fortawesome/free-solid-svg-icons"

import { AuditRecord } from "types/questionnaire.types"
import { HashChip } from "shared/ui/HashChip/HashChip.component"
import { ChangeRow } from "./ChangeRow.component"
import { MergeRecordDetails } from "./DataRecordDetails.component"
import { changeJumpTarget, collapseRemaps } from "../utils/historyLabels"

// Why a fork has no questionnaire version: the next save then lists every question as added.
const forkNoVersion = (rec: AuditRecord): string | null => {
  if (rec.parseStatus === "no_qnr") return "The project had no questionnaire when it was copied."
  if (rec.parseStatus === "duplicate_label")
    return `Two questions shared the label "${rec.label ?? ""}" when it was copied, so no version was taken.`
  if (rec.parseStatus === "parse_failed")
    return "The questionnaire couldn't be read when it was copied, so no version was taken."
  return null
}

/** A tracker root's record: another project joined or left this tracker. */
export const TrackerMemberRecordDetails: React.FC<{ rec: AuditRecord }> = ({ rec }) => {
  const joined = rec.action === "wave.linked"
  return (
    <div className="rounded-md border border-violet-100 bg-violet-50/60 px-3 py-2 text-[12px] text-slate-600">
      <div className="flex items-center gap-1.5 font-medium text-violet-700">
        <FontAwesomeIcon icon={joined ? faLink : faLinkSlash} className="h-3 w-3" />
        {joined ? "A wave joined this tracker" : "A wave left this tracker"}
      </div>
      {rec.memberProjectId && (
        <div className="mt-1.5 font-mono text-[10px] text-slate-400">
          project {rec.memberProjectId.slice(0, 8)}…
        </div>
      )}
    </div>
  )
}

/** This project's own tracker records: forked as a wave, linked to a tracker, or left it. */
export const WaveRecordDetails: React.FC<{ rec: AuditRecord }> = ({ rec }) => {
  const forked = rec.action === "wave.forked"
  const left = rec.action === "wave.unlinked"
  const noVersion = forked ? forkNoVersion(rec) : null
  return (
    <div
      className={`rounded-md border px-3 py-2 text-[12px] text-slate-600 ${
        forked ? "border-sky-100 bg-sky-50/60" : "border-violet-100 bg-violet-50/60"
      }`}
    >
      <div className={`flex items-center gap-1.5 font-medium ${forked ? "text-sky-700" : "text-violet-700"}`}>
        <FontAwesomeIcon icon={forked ? faCodeBranch : left ? faLinkSlash : faLink} className="h-3 w-3" />
        {forked ? "Forked as a new tracker wave" : left ? "Left a tracker" : "Linked to a tracker"}
      </div>
      <div className="mt-1 text-slate-500">
        {forked
          ? "This starts a fresh history for this wave, linked to the project it was copied from."
          : left
            ? "This project left its tracker. Its own history is unchanged."
            : "This project was added to a tracker. Its own history is unchanged; this marks the link."}
      </div>
      {noVersion && (
        <div className="mt-1 text-slate-500">
          {noVersion} The next save lists every question as added.
        </div>
      )}
      {rec.parentProjectId && (
        <div className="mt-1.5 font-mono text-[10px] text-slate-400">
          {forked ? "parent" : "linked to"} {rec.parentProjectId.slice(0, 8)}…
          {forked &&
            (rec.forkedFromRecordHash
              ? ` @ #${rec.forkedFromRecordHash.slice(0, 10)}`
              : " (source had no prior history)")}
        </div>
      )}
    </div>
  )
}

/** An export: the file, the IDs it was made from, and its captured lineage. */
export const ExportRecordDetails: React.FC<{
  rec: AuditRecord
  onViewLineage?: (hash: string) => void
}> = ({ rec, onViewLineage }) => (
  <div className="rounded-md border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-[12px] text-slate-600">
    <div className="flex items-center gap-1.5 font-medium text-emerald-700">
      <FontAwesomeIcon icon={faFileExport} className="h-3 w-3" />
      Deliverable exported
    </div>
    <div className="mt-1 text-slate-500">
      {rec.filename || "No file name"}
      {rec.format ? ` · ${rec.format}` : ""}
    </div>
    {rec.driftAtExport != null && rec.driftAtExport > 0 && (
      <div className="mt-1 text-[11px] font-medium text-amber-700">
        ⚠ exported while the data differed from the questionnaire
      </div>
    )}
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {rec.file?.sha256 && (
        <>
          <HashChip hash={rec.file.sha256} label="file" length={12} />
          {rec.file.sizeBytes != null && (
            <span className="text-[10px] text-slate-400">
              {rec.file.sizeBytes.toLocaleString()} bytes
            </span>
          )}
        </>
      )}
      {rec.projectVersion && (
        <HashChip hash={rec.projectVersion} label="version ID" length={10} />
      )}
      {rec.structuralKey && (
        <HashChip hash={rec.structuralKey} label="structure ID" length={10} />
      )}
      {rec.exportMap && (
        <HashChip
          hash={rec.exportMap.hash}
          label={rec.exportMap.name ? `map ${rec.exportMap.name}` : "map"}
          length={10}
        />
      )}
      {rec.lineage?.hash && (
        <HashChip
          hash={rec.lineage.hash}
          label={`lineage${rec.lineage.nodeCount != null ? ` (${rec.lineage.nodeCount})` : ""}`}
          length={10}
        />
      )}
    </div>
    {rec.lineage?.hash && onViewLineage && (
      <button
        type="button"
        onClick={() => onViewLineage(rec.lineage!.hash)}
        className="mt-1 text-[11px] text-slate-500 underline"
      >
        View captured lineage
      </button>
    )}
  </div>
)

/** A data import that compared nothing: no questionnaire, or no datamap. */
export const ImportRecordDetails: React.FC<{ rec: AuditRecord }> = ({ rec }) => (
  <div className="rounded-md border border-slate-100 bg-slate-50/60 px-3 py-2 text-[12px] text-slate-600">
    <div className="text-slate-500">
      {rec.parseStatus === "no_qnr"
        ? "The project had no questionnaire when the data was imported, so nothing was compared. The import and the ID of its source file are recorded."
        : "This import had no datamap to compare with the questionnaire. The import and the ID of its source file are recorded."}
    </div>
    {rec.dataHash && (
      <div className="mt-1.5">
        <HashChip hash={rec.dataHash} label="source file" length={12} />
      </div>
    )}
  </div>
)

/** A version's (or an import's) changes, one row each; a row can jump to its question. */
export const VersionChangesDetails: React.FC<{
  rec: AuditRecord
  onJumpToQuestion?: (label: string) => void
}> = ({ rec, onJumpToQuestion }) =>
  rec.changes && rec.changes.length > 0 ? (
    <ul className="mb-1">
      {collapseRemaps(rec.changes).map((c, i) => {
        // A variant change ("UK/Q1") jumps to its question ("Q1").
        const target = changeJumpTarget(c)
        return (
          <ChangeRow
            key={`${c.path}-${c.kind}-${i}`}
            change={c}
            onJump={onJumpToQuestion && target ? () => onJumpToQuestion(target) : undefined}
          />
        )
      })}
    </ul>
  ) : (
    <p className="py-1 text-[12px] italic text-slate-400">No changes to the questions in this version.</p>
  )

/** The body of an opened history entry, by the kind of entry. */
export const HistoryRecordDetails: React.FC<{
  rec: AuditRecord
  onJumpToQuestion?: (label: string) => void
  onViewLineage?: (hash: string) => void
  onViewAncestry?: () => void
}> = ({ rec, onJumpToQuestion, onViewLineage, onViewAncestry }) => {
  if (rec.action === "data.merged") {
    return <MergeRecordDetails rec={rec} onViewLineage={onViewLineage} onViewAncestry={onViewAncestry} />
  }
  if (rec.memberProjectId && (rec.action === "wave.linked" || rec.action === "wave.unlinked")) {
    return <TrackerMemberRecordDetails rec={rec} />
  }
  if (rec.action === "wave.forked" || rec.action === "wave.linked" || rec.action === "wave.unlinked") {
    return <WaveRecordDetails rec={rec} />
  }
  if (rec.action === "deliverable.exported") {
    return <ExportRecordDetails rec={rec} onViewLineage={onViewLineage} />
  }
  if (rec.action === "data.imported" && (rec.parseStatus === "no_map" || rec.parseStatus === "no_qnr")) {
    return <ImportRecordDetails rec={rec} />
  }
  return <VersionChangesDetails rec={rec} onJumpToQuestion={onJumpToQuestion} />
}

export default HistoryRecordDetails
